import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as Icon from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../core/data/redux/store';
import {
  createProposal,
  updateProposal,
  getAllProposals,
} from '../../../../services/proposalServices';
import { getAllProjects } from '../../../../services/projectServices';
import { getAllLeads } from '../../../../services/leadServices';
import { getAllPartnerUsers } from '../../../../services/partnerServices';
import {
  ProposalStatus,
  CurrencyType,
  HandoffType,
  Timeline,
  Stage,
  StageItem,
  Deliverable,
  Table,
  ProposalValidationErrors,
} from '../../../../services/proposalServices/types';

import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { Card } from 'primereact/card';
// Add Ant Design imports
import { Table as AntTable, Space, Tag, Typography, Divider, Row, Col } from 'antd';
import { DragOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const style = `
  .dropdown-panel {
    z-index: 9999 !important;
  }
  .p-dropdown-panel {
    z-index: 9999 !important;
  }
  .step-indicator {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;
    padding: 0.5em;
  }
  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    cursor: pointer;
  }
  .step-number {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin-bottom: 0.5rem;
    font-size: 1.1em;
  }
  .step-active {
    background-color: #007bff;
    color: #fff !important;
  }
  .step-completed {
    background-color: #28a745;
    color: #fff !important;
  }
  .step-pending {
    background-color: #e9ecef;
    color: #6c757d !important;
  }
  .step-title {
    font-size: 1em;
    text-align: center;
    color: #222 !important;
    font-weight: 500;
    background: none !important;
    box-shadow: none !important;
    text-shadow: none !important;
    padding: 0;
    margin: 0;
  }
  .step-active .step-title,
  .step-completed .step-title {
    color: #222 !important;
    background: none !important;
    font-weight: 600;
    text-shadow: none !important;
  }
  .form-step {
    display: none;
  }
  .form-step.active {
    display: block;
  }
  .form-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
  }
  .validation-error {
    color: #dc3545;
    font-size: 0.875em;
    margin-top: 0.25rem;
  }
  .field-group {
    margin-bottom: 1.5rem;
  }
  .field-label {
    font-weight: 600;
    margin-bottom: 0.5rem;
    display: block;
  }
  .draft-save-indicator {
    position: fixed;
    top: 1rem;
    right: 1rem;
    background: #28a745;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-size: 0.875rem;
    z-index: 100;
    opacity: 0;
    transition: opacity 0.3s;
  }
  .draft-save-indicator.show {
    opacity: 1;
  }
  .stage-item {
    padding: 12px;
    margin-bottom: 8px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: #f8f9fa;
  }
  .stage-item:hover {
    background: #e9ecef;
  }
  .deliverable-item {
    padding: 12px;
    margin-bottom: 8px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: #f8f9fa;
  }
  .deliverable-item:hover {
    background: #e9ecef;
  }
  .table-item {
    padding: 12px;
    margin-bottom: 8px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: #f8f9fa;
  }
  .table-item:hover {
    background: #e9ecef;
  }
  .stage-items-section {
    border-left: 3px solid #007bff;
    background: #f8f9fa;
  }
  .stage-item-detail {
    border-left: 2px solid #28a745;
  }
  .stage-item-detail:hover {
    background: #e9ecef;
  }
  .drag-handle {
    cursor: grab;
    color: #6c757d;
    margin-right: 8px;
  }
  .drag-handle:active {
    cursor: grabbing;
  }
  .review-section {
    margin-bottom: 1.5rem;
    padding: 1rem;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: #f8f9fa;
  }
  .review-section h5 {
    color: #495057;
    margin-bottom: 0.75rem;
    border-bottom: 2px solid #07;
    padding-bottom: 0.5rem;
  }
  .review-section p {
    margin-bottom: 0.5rem;
  }
  .edit-link {
    color: #007bff;
    text-decoration: none;
    font-size: 0.875em;
    margin-top: 0.5rem;
    display: inline-block;
  }
  .edit-link:hover {
    text-decoration: underline;
  }
`;

const statusOptions = [
  { label: 'Pending', value: ProposalStatus.PENDING },
  { label: 'Accepted', value: ProposalStatus.ACCEPTED },
  { label: 'Rejected', value: ProposalStatus.REJECTED },
];

const currencyOptions = [
  { label: 'USD', value: CurrencyType.USD },
  { label: 'EUR', value: CurrencyType.EUR },
  { label: 'TRY', value: CurrencyType.TRY },
  { label: 'GBP', value: CurrencyType.GBP },
];

const handoffOptions = [
  { label: 'Full Handoff', value: HandoffType.FULL_HANDOFF },
];

const STEPS = [
  { id: 1, title: 'Basic Info', description: 'Project, Status, Lead' },
  { id: 2, title: 'Financial', description: 'Costs, Timeline' },
  { id: 3, title: 'Content', description: 'Overview, Scope, Quotation' },
  { id: 4, title: 'Planning', description: 'Stages, Deliverables' },
  { id: 5, title: 'Cost Breakdown', description: 'Tables' },
  { id: 6, title: 'Review', description: 'Review & Submit' },
];

const ProposalsModal = ({
  selectedProposal,
  setIsAddModalOpen,
  isAddModalOpen,
  isEditModalOpen,
  setIsEditModalOpen,
  onProposalCreated,
}: {
  selectedProposal: any | null;
  setIsAddModalOpen: (value: boolean) => void;
  isAddModalOpen: boolean;
  isEditModalOpen: boolean;
  setIsEditModalOpen: (value: boolean) => void;
  onProposalCreated?: (proposal: any) => void;
}) => {
  console.log('selectedProposal', selectedProposal);
  const toast = useRef<Toast>(null);
  const { proposalCreated, proposalUpdated, isError, errorMessage } =
    useSelector((state: any) => state.proposal);
  const { projects } = useSelector((state: any) => state.project);
  const { leads } = useSelector((state: any) => state.lead);
  const { partnerUsers } = useSelector((state: any) => state.partner);
  
  const [projectOptions, setProjectOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [leadOptions, setLeadOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [partnerOptions, setPartnerOptions] = useState<Array<{ label: string; value: string }>>([]);

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [validationErrors, setValidationErrors] = useState<ProposalValidationErrors>({});
  const [showDraftSaved, setShowDraftSaved] = useState(false);

  const [proposal, setProposal] = useState({
    project_id: '',
    status: ProposalStatus.PENDING,
    overview: '',
    scope_of_work: '',
    quotation_details: '',
    not_included_in_price: '',
    currency_type: CurrencyType.USD,
    price: 0,
    user_id: userData.id || '',
    project_lead_id: '',
    expected_project_time: 0,
    expected_project_cost: 0,
    expected_project_cost_currency: CurrencyType.USD,
    timelines: [] as Timeline[],
    stages: [] as Stage[],
    deliverables: [] as Deliverable[],
    tables: [] as Table[],
  });

  const [updatedProposal, setUpdatedProposal] = useState<any>({
    id: selectedProposal?.id || '',
    project_id: selectedProposal?.project_id || '',
    status: selectedProposal?.status || ProposalStatus.PENDING,
    overview: selectedProposal?.overview || '',
    scope_of_work: selectedProposal?.scope_of_work || '',
    quotation_details: selectedProposal?.quotation_details || '',
    not_included_in_price: selectedProposal?.not_included_in_price || '',
    currency_type: selectedProposal?.currency_type || CurrencyType.USD,
    price: selectedProposal?.price || 0,
    user_id: userData.id || '',
    project_lead_id: selectedProposal?.project_lead_id || '',
    expected_project_time: selectedProposal?.expected_project_time || 0,
    expected_project_cost: selectedProposal?.price || 0, // Sync with price
    expected_project_cost_currency: selectedProposal?.currency_type || CurrencyType.USD, // Sync with currency_type
    timelines: selectedProposal?.timelines || [],
    stages: selectedProposal?.stages || [],
    deliverables: selectedProposal?.deliverables || [],
    tables: selectedProposal?.tables || [],
  });

  // Add these states after proposal/updatedProposal state
  const [newStage, setNewStage] = useState<Stage>({ stage_number: 0, main_item: { title: '', detail: '' }, items: [] });
  const [newStageItem, setNewStageItem] = useState<StageItem>({ title: '', detail: '' });
  const [editStageIndex, setEditStageIndex] = useState<number | null>(null);
  const [editStageItemIndex, setEditStageItemIndex] = useState<{stage: number, item: number} | null>(null);
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());

  const [newDeliverable, setNewDeliverable] = useState<Deliverable>({ service: '', led_by: '', handoff: HandoffType.FULL_HANDOFF });
  const [editDeliverableIndex, setEditDeliverableIndex] = useState<number | null>(null);

  const [newTable, setNewTable] = useState<Table>({ service: '', monthly_cost: 0, monthly_cost_currency: CurrencyType.USD, months: 0, total_cost: 0, total_cost_currency: CurrencyType.USD });
  const [editTableIndex, setEditTableIndex] = useState<number | null>(null);

  const [newTimeline, setNewTimeline] = useState<Timeline>({ 
    proposal_id: '', 
    service: '', 
    time: 0, 
    possible_delay: 0 
  });
  const [editTimelineIndex, setEditTimelineIndex] = useState<number | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  // Add drag state for stages and deliverables
  const [draggedStageIndex, setDraggedStageIndex] = useState<number | null>(null);
  const [draggedDeliverableIndex, setDraggedDeliverableIndex] = useState<number | null>(null);
  // Add drag state for cost breakdown
  const [draggedTableIndex, setDraggedTableIndex] = useState<number | null>(null);

  const dispatch = useDispatch<AppDispatch>();

  // Ensure updatedProposal is set when editing
  useEffect(() => {
    if (isEditModalOpen && selectedProposal) {
      setUpdatedProposal({
        id: selectedProposal.id || '',
        project_id: selectedProposal.project_id || '',
        status: selectedProposal.status || ProposalStatus.PENDING,
        overview: selectedProposal.overview || '',
        scope_of_work: selectedProposal.scope_of_work || '',
        quotation_details: selectedProposal.quotation_details || '',
        not_included_in_price: selectedProposal.not_included_in_price || '',
        currency_type: selectedProposal.currency_type || CurrencyType.USD,
        price: selectedProposal.price || 0,
        user_id: selectedProposal.user_id || userData.id || '',
        project_lead_id: selectedProposal.project_lead_id || '',
        expected_project_time: selectedProposal.expected_project_time || 0,
        expected_project_cost: selectedProposal.price || 0, // Sync with price
        expected_project_cost_currency: selectedProposal.currency_type || CurrencyType.USD, // Sync with currency_type
        timelines: selectedProposal.timelines || [],
        stages: selectedProposal.stages || [],
        deliverables: selectedProposal.deliverables || [],
        tables: selectedProposal.tables || [],
      });
      // Auto-expand stages that have items when editing
      const stagesWithItems = new Set<number>();
      if (selectedProposal.stages) {
        selectedProposal.stages.forEach((stage: Stage, index: number) => {
          if (stage.items && stage.items.length > 0) {
            stagesWithItems.add(index);
          }
        });
      }
      setExpandedStages(stagesWithItems);
    }
  }, [selectedProposal, isEditModalOpen]);

  // Load data on mount
  useEffect(() => {
    dispatch(getAllProjects());
    dispatch(getAllLeads());
    dispatch(getAllPartnerUsers());
  }, [dispatch]);

  // Set up project options
  useEffect(() => {
    if (projects && Array.isArray(projects)) {
      const options = projects
        .filter((project: any) => project && typeof project === 'object' && project.name)
        .map((project: any) => ({
          label: project.name,
          value: project.id,
        }));
      setProjectOptions(options);
    }
  }, [projects]);

  // Set up lead options
  useEffect(() => {
    if (leads) {
      const options = leads.map((lead: any) => ({
        label: `${lead.first_name} ${lead.last_name}`,
        value: lead.id,
      }));
      setLeadOptions(options);
    }
  }, [leads]);

  // Set up partner options
  useEffect(() => {
    if (partnerUsers) {
      const options = partnerUsers.map((partner: any) => ({
        label: `${partner.first_name} ${partner.last_name}`,
        value: partner.id,
      }));
      setPartnerOptions(options);
    }
  }, [partnerUsers]);

  // Auto-save draft functionality
  useEffect(() => {
    const saveDraft = () => {
      const draftData = isEditModalOpen ? updatedProposal : proposal;
      localStorage.setItem('proposal_draft', JSON.stringify(draftData));
      setShowDraftSaved(true);
      setTimeout(() => setShowDraftSaved(false), 2000);
    };

    const interval = setInterval(saveDraft, 30000); // Auto-save every 30 seconds
    return () => clearInterval(interval);
  }, [proposal, updatedProposal, isEditModalOpen]);

  // --- 1. Always reset form state on open, never load draft automatically ---
  useEffect(() => {
    if (isAddModalOpen && !isEditModalOpen) {
      setProposal({
        project_id: '',
        status: ProposalStatus.PENDING,
        overview: '',
        scope_of_work: '',
        quotation_details: '',
        not_included_in_price: '',
        currency_type: CurrencyType.USD,
        price: 0,
        user_id: userData.id || '',
        project_lead_id: '',
        expected_project_time: 0,
        expected_project_cost: 0,
        expected_project_cost_currency: CurrencyType.USD,
        timelines: [] as Timeline[],
        stages: [] as Stage[],
        deliverables: [] as Deliverable[],
        tables: [] as Table[],
      });
      setCurrentStep(1);
      setCompletedSteps(new Set());
      setValidationErrors({});
      setExpandedStages(new Set());
      // Do NOT load draft automatically
    }
  }, [isAddModalOpen, isEditModalOpen, userData.id]);

  // Handle successful submission and close modal
  useEffect(() => {
    if (proposalCreated || proposalUpdated) {
      // Clear draft
      localStorage.removeItem('proposal_draft');
      
      // Show success message
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: proposalCreated ? 'Proposal created successfully!' : 'Proposal updated successfully!',
        life: 3000,
      });
      
      // Close modal
      if (proposalCreated) {
        setIsAddModalOpen(false);
        // Call callback if provided
        if (onProposalCreated) {
          onProposalCreated(proposal);
        }
      } else if (proposalUpdated) {
        setIsEditModalOpen(false);
      }
      
      // Reset form state for create mode
      if (proposalCreated) {
        setProposal({
          project_id: '',
          status: ProposalStatus.PENDING,
          overview: '',
          scope_of_work: '',
          quotation_details: '',
          not_included_in_price: '',
          currency_type: CurrencyType.USD,
          price: 0,
          user_id: userData.id || '',
          project_lead_id: '',
          expected_project_time: 0,
          expected_project_cost: 0,
          expected_project_cost_currency: CurrencyType.USD,
          timelines: [] as Timeline[],
          stages: [] as Stage[],
          deliverables: [] as Deliverable[],
          tables: [] as Table[],
        });
        setCurrentStep(1);
        setCompletedSteps(new Set());
        setValidationErrors({});
      }
    }
  }, [proposalCreated, proposalUpdated, setIsAddModalOpen, setIsEditModalOpen, onProposalCreated, proposal, userData.id]);

  // Show error messages
  useEffect(() => {
    if (isError && errorMessage) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
    }
  }, [isError, errorMessage]);

  // Validate current step
  const validateStep = (step: number): boolean => {
    const errors: ProposalValidationErrors = {};
    const currentData = isEditModalOpen ? updatedProposal : proposal;

    switch (step) {
      case 1: // Basic Info
        if (!currentData.project_id) errors.project_id = 'Project is required';
        if (!currentData.project_lead_id) errors.project_lead_id = 'Project Lead is required';
        break;
      case 2: // Financial
        if (!currentData.price || currentData.price <= 0) errors.price = 'Valid price is required';
        if (!currentData.expected_project_time || currentData.expected_project_time <= 0) errors.expected_project_time = 'Valid project time is required';
        break;
      case 3: // Content
        if (!currentData.overview?.trim()) errors.overview = 'Overview is required';
        if (!currentData.scope_of_work?.trim()) errors.scope_of_work = 'Scope of work is required';
        if (!currentData.quotation_details?.trim()) errors.quotation_details = 'Quotation details are required';
        break;
      case 4: // Planning
        if (!currentData.stages?.length) errors.stages = 'At least one stage is required';
        if (!currentData.deliverables?.length) errors.deliverables = 'At least one deliverable is required';
        break;
      case 5: // Cost Breakdown
        if (!currentData.tables?.length) errors.tables = 'At least one cost table is required';
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Navigate to step
  const goToStep = (step: number) => {
    if (step < currentStep || completedSteps.has(step)) {
      setCurrentStep(step);
    }
  };

  // Next step
  const nextStep = () => {
    if (validateStep(currentStep)) {
      const newCompletedSteps = new Set(completedSteps);
      newCompletedSteps.add(currentStep);
      setCompletedSteps(newCompletedSteps);
      if (currentStep < STEPS.length) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  // Previous step
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    try {
      setIsLoading(true);
      const proposalData = isEditModalOpen ? updatedProposal : proposal;

      if (isEditModalOpen) {
        // PUT (Update): Must include proposal_id in nested objects
        if (proposalData.timelines && proposalData.timelines.length > 0) {
          proposalData.timelines = proposalData.timelines.map((timeline: Timeline) => ({
            ...timeline,
            proposal_id: selectedProposal.id
          }));
        }
        if (proposalData.stages && proposalData.stages.length > 0) {
          proposalData.stages = proposalData.stages.map((stage: Stage) => ({
            ...stage,
            proposal_id: selectedProposal.id
          }));
        }
        if (proposalData.deliverables && proposalData.deliverables.length > 0) {
          proposalData.deliverables = proposalData.deliverables.map((deliverable: Deliverable) => ({
            ...deliverable,
            proposal_id: selectedProposal.id
          }));
        }
        if (proposalData.tables && proposalData.tables.length > 0) {
          proposalData.tables = proposalData.tables.map((table: Table) => ({
            ...table,
            proposal_id: selectedProposal.id
          }));
        }
        
        await dispatch(updateProposal(selectedProposal.id, proposalData));
      } else {
        // POST (Create): Don't include proposal_id in nested objects
        if (proposalData.timelines && proposalData.timelines.length > 0) {
          proposalData.timelines = proposalData.timelines.map((timeline: Timeline) => {
            const { proposal_id, ...timelineWithoutId } = timeline;
            return timelineWithoutId;
          });
        }
        if (proposalData.stages && proposalData.stages.length > 0) {
          proposalData.stages = proposalData.stages.map((stage: Stage) => {
            const { proposal_id, ...stageWithoutId } = stage;
            return stageWithoutId;
          });
        }
        if (proposalData.deliverables && proposalData.deliverables.length > 0) {
          proposalData.deliverables = proposalData.deliverables.map((deliverable: Deliverable) => {
            const { proposal_id, ...deliverableWithoutId } = deliverable;
            return deliverableWithoutId;
          });
        }
        if (proposalData.tables && proposalData.tables.length > 0) {
          proposalData.tables = proposalData.tables.map((table: Table) => {
            const { proposal_id, ...tableWithoutId } = table;
            return tableWithoutId;
          });
        }
        
        await dispatch(createProposal(proposalData));
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: string, value: any) => {
    const updateData = (prev: any) => ({ ...prev, [field]: value });
    
    if (isEditModalOpen) {
      setUpdatedProposal(updateData);
    } else {
      setProposal(updateData);
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="step-indicator">
      {STEPS.map((step) => {
        const isActive = step.id === currentStep;
        const isCompleted = completedSteps.has(step.id);
        const isPending = !isActive && !isCompleted;

        return (
          <div key={step.id} className="step" onClick={() => goToStep(step.id)}>
            <div className={`step-number ${isActive ? 'step-active' : isCompleted ? 'step-completed' : 'step-pending'}`}>
              {isCompleted ? <Icon.Check size={16} /> : step.id}
            </div>
            <div className={`step-title ${isActive ? 'step-active' : isCompleted ? 'step-completed' : ''}`}>
              {step.title}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render step content
  const renderStepContent = () => {
    const currentData = isEditModalOpen ? updatedProposal : proposal;

    switch (currentStep) {
      case 1:
        return (
          <div className="form-step active">
            <h4>Basic Information</h4>
            <div className="field-group">
              <label className="field-label">Project *</label>
              <Dropdown
                value={currentData.project_id}
                options={projectOptions}
                onChange={(e) => handleInputChange('project_id', e.value)}
                placeholder="Select Project"
                className={validationErrors.project_id ? 'p-invalid' : ''}
              />
              {validationErrors.project_id && (
                <div className="validation-error">{validationErrors.project_id}</div>
              )}
            </div>

            <div className="field-group">
              <label className="field-label">Status</label>
              <Dropdown
                value={currentData.status}
                options={statusOptions}
                onChange={(e) => handleInputChange('status', e.value)}
                placeholder="Select Status"
                className={validationErrors.status ? 'p-invalid' : ''}
              />
              {validationErrors.status && (
                <div className="validation-error">{validationErrors.status}</div>
              )}
            </div>

            <div className="field-group">
              <label className="field-label">Project Lead *</label>
              <Dropdown
                value={currentData.project_lead_id}
                options={leadOptions}
                onChange={(e) => handleInputChange('project_lead_id', e.value)}
                placeholder="Select Project Lead"
                className={validationErrors.project_lead_id ? 'p-invalid' : ''}
              />
              {validationErrors.project_lead_id && (
                <div className="validation-error">{validationErrors.project_lead_id}</div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="form-step active">
            <h4>Financial Information</h4>
            <div className="field-group">
              <label className="field-label">Price *</label>
              <InputNumber
                value={currentData.price}
                onValueChange={(e) => {
                  handleInputChange('price', e.value);
                  // Automatically set expected_project_cost to match price
                  handleInputChange('expected_project_cost', e.value);
                }}
                mode="currency"
                currency={currentData.currency_type}
                className={validationErrors.price ? 'p-invalid' : ''}
              />
              {validationErrors.price && (
                <div className="validation-error">{validationErrors.price}</div>
              )}
            </div>

            <div className="field-group">
              <label className="field-label">Currency</label>
              <Dropdown
                value={currentData.currency_type}
                options={currencyOptions}
                onChange={(e) => {
                  handleInputChange('currency_type', e.value);
                  // Automatically set expected_project_cost_currency to match currency_type
                  handleInputChange('expected_project_cost_currency', e.value);
                }}
                placeholder="Select Currency"
                className={validationErrors.currency_type ? 'p-invalid' : ''}
              />
              {validationErrors.currency_type && (
                <div className="validation-error">{validationErrors.currency_type}</div>
              )}
            </div>

            <div className="field-group">
              <label className="field-label">Expected Project Cost</label>
              <InputNumber
                value={currentData.expected_project_cost}
                onValueChange={(e) => handleInputChange('expected_project_cost', e.value)}
                mode="currency"
                currency={currentData.expected_project_cost_currency}
                className=""
                disabled={true}
              />
              <small className="text-muted">Automatically set to match the price above</small>
            </div>

            <div className="field-group">
              <label className="field-label">Expected Project Time (weeks) *</label>
              <InputNumber
                value={currentData.expected_project_time}
                onValueChange={(e) => handleInputChange('expected_project_time', e.value)}
                min={1}
                className={validationErrors.expected_project_time ? 'p-invalid' : ''}
              />
              {validationErrors.expected_project_time && (
                <div className="validation-error">{validationErrors.expected_project_time}</div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="form-step active">
            <h4>Content</h4>
            <div className="field-group">
              <label className="field-label">Overview *</label>
              <textarea
                value={currentData.overview}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('overview', e.target.value)}
                rows={4}
                className={`form-control ${validationErrors.overview ? 'is-invalid' : ''}`}
                placeholder="Enter project overview..."
              />
              {validationErrors.overview && (
                <div className="validation-error">{validationErrors.overview}</div>
              )}
            </div>

            <div className="field-group">
              <label className="field-label">Scope of Work *</label>
              <textarea
                value={currentData.scope_of_work}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('scope_of_work', e.target.value)}
                rows={4}
                className={`form-control ${validationErrors.scope_of_work ? 'is-invalid' : ''}`}
                placeholder="Enter scope of work..."
              />
              {validationErrors.scope_of_work && (
                <div className="validation-error">{validationErrors.scope_of_work}</div>
              )}
            </div>

            <div className="field-group">
              <label className="field-label">Quotation Details *</label>
              <textarea
                value={currentData.quotation_details}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('quotation_details', e.target.value)}
                rows={4}
                className={`form-control ${validationErrors.quotation_details ? 'is-invalid' : ''}`}
                placeholder="Enter quotation details..."
              />
              {validationErrors.quotation_details && (
                <div className="validation-error">{validationErrors.quotation_details}</div>
              )}
            </div>

            <div className="field-group">
              <label className="field-label">Not Included in Price</label>
              <textarea
                value={currentData.not_included_in_price}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('not_included_in_price', e.target.value)}
                rows={3}
                className="form-control"
                placeholder="Enter items not included in price..."
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="form-step active">
            <h4>Planning</h4>
            
            {/* Timelines Management */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Timelines</h5>
                <Button 
                  icon="pi pi-plus" 
                  className="p-button-sm p-button-outlined" 
                  onClick={() => {
                    setEditTimelineIndex(null);
                    setNewTimeline({ proposal_id: currentData.id || '', service: '', time: 0, possible_delay: 0 });
                  }}
                  label="Add Timeline"
                />
              </div>
              
              {currentData.timelines && currentData.timelines.length > 0 && (
                <div className="timelines-list mb-3">
                  {currentData.timelines.map((timeline: Timeline, idx: number) => (
                    <div key={idx} className="timeline-item mb-2 p-3 border rounded">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center flex-grow-1">
                          <span className="drag-handle me-3" title="Drag to reorder">≡</span>
                          <div className="flex-grow-1">
                            {editTimelineIndex === idx ? (
                              <div className="row g-2">
                                <div className="col-md-4">
                                  <InputText 
                                    value={newTimeline.service} 
                                    onChange={e => setNewTimeline(t => ({...t, service: e.target.value}))} 
                                    placeholder="Service" 
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-md-4">
                                  <InputNumber 
                                    value={newTimeline.time} 
                                    onValueChange={e => setNewTimeline(t => ({...t, time: e.value || 0}))} 
                                    placeholder="Time (weeks)" 
                                    min={1}
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-md-4">
                                  <InputNumber 
                                    value={newTimeline.possible_delay} 
                                    onValueChange={e => setNewTimeline(t => ({...t, possible_delay: e.value || 0}))} 
                                    placeholder="Possible Delay (weeks)" 
                                    min={0}
                                    className="w-100"
                                  />
                                </div>
                              </div>
                            ) : (
                              <div>
                                <strong>{timeline.service}</strong> - {timeline.time} weeks
                                {timeline.possible_delay > 0 && ` (${timeline.possible_delay} weeks delay possible)`}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          {editTimelineIndex === idx ? (
                            <>
                              <button 
                                className="btn btn-success btn-sm me-2" 
                                onClick={() => {
                                  const updated = [...currentData.timelines];
                                  const timelineToUpdate = {
                                    ...newTimeline,
                                    proposal_id: currentData.id || timeline.proposal_id || '' // Preserve proposal_id
                                  };
                                  updated[idx] = timelineToUpdate;
                                  handleInputChange('timelines', updated);
                                  setEditTimelineIndex(null);
                                  setNewTimeline({ proposal_id: currentData.id || '', service: '', time: 0, possible_delay: 0 });
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                              >
                                ✓
                              </button>
                              <button 
                                className="btn btn-secondary btn-sm me-2" 
                                onClick={() => {
                                  setEditTimelineIndex(null);
                                  setNewTimeline({ proposal_id: currentData.id || '', service: '', time: 0, possible_delay: 0 });
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                className="btn btn-outline-primary btn-sm me-2" 
                                onClick={() => {
                                  setEditTimelineIndex(idx);
                                  setNewTimeline({
                                    ...timeline,
                                    proposal_id: timeline.proposal_id || currentData.id || '' // Preserve existing proposal_id
                                  });
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                title="Edit Timeline"
                              >
                                ✏️
                              </button>
                              <button 
                                className="btn btn-outline-danger btn-sm" 
                                onClick={() => {
                                  const updated = [...currentData.timelines];
                                  updated.splice(idx, 1);
                                  handleInputChange('timelines', updated);
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                title="Delete Timeline"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Timeline Form */}
              {editTimelineIndex === null && (
                <div className="card p-3">
                  <h6 className="mb-3">Add New Timeline</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Service</label>
                      <InputText 
                        value={newTimeline.service} 
                        onChange={e => setNewTimeline(t => ({...t, service: e.target.value}))} 
                        placeholder="Enter service name" 
                        className="w-100"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Time (weeks)</label>
                      <InputNumber 
                        value={newTimeline.time} 
                        onValueChange={e => setNewTimeline(t => ({...t, time: e.value || 0}))} 
                        placeholder="Enter time in weeks" 
                        min={1}
                        className="w-100"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Possible Delay (weeks)</label>
                      <InputNumber 
                        value={newTimeline.possible_delay} 
                        onValueChange={e => setNewTimeline(t => ({...t, possible_delay: e.value || 0}))} 
                        placeholder="Enter possible delay" 
                        min={0}
                        className="w-100"
                      />
                    </div>
                    <div className="col-12">
                      <Button 
                        icon="pi pi-plus" 
                        className="p-button-primary" 
                        onClick={() => {
                          if (newTimeline.service && newTimeline.time > 0) {
                            const updated = [...(currentData.timelines || [])];
                            const timelineToAdd = {
                              ...newTimeline,
                              proposal_id: currentData.id || '' // Set proposal_id if available
                            };
                            updated.push(timelineToAdd);
                            handleInputChange('timelines', updated);
                            setNewTimeline({ proposal_id: currentData.id || '', service: '', time: 0, possible_delay: 0 });
                          }
                        }}
                        label="Add Timeline"
                        disabled={!newTimeline.service || !newTimeline.time}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Stages Management */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Stages</h5>
                <Button 
                  icon="pi pi-plus" 
                  className="p-button-sm p-button-outlined" 
                  onClick={() => {
                    setEditStageIndex(null);
                    setNewStage({ stage_number: (currentData.stages?.length || 0) + 1, main_item: { title: '', detail: '' }, items: [] });
                  }}
                  label="Add Stage"
                />
              </div>
              
              {currentData.stages && currentData.stages.length > 0 && (
                <div className="stages-list mb-3">
                  {currentData.stages.map((stage: Stage, stageIdx: number) => (
                    <div key={stageIdx} className="stage-item mb-3 p-3 border rounded">
                      {/* Stage Header */}
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center flex-grow-1">
                          <span className="drag-handle me-3" title="Drag to reorder">≡</span>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                              <strong className="me-2">Stage {stage.stage_number}:</strong>
                              {editStageIndex === stageIdx ? (
                                <>
                                  <InputText 
                                    value={newStage.main_item?.title || ''} 
                                    onChange={e => setNewStage(s => ({...s, main_item: { title: e.target.value, detail: s.main_item?.detail || '' }}))} 
                                    placeholder="Stage Title"
                                    className="me-2"
                                    style={{ width: '200px' }}
                                  />
                                </>
                              ) : (
                                <span className="me-2">{stage.main_item?.title || 'Untitled'}</span>
                              )}
                            </div>
                            <div className="d-flex align-items-center">
                              {editStageIndex === stageIdx ? (
                                <InputText 
                                  value={newStage.main_item?.detail || ''} 
                                  onChange={e => setNewStage(s => ({...s, main_item: { title: s.main_item?.title || '', detail: e.target.value }}))} 
                                  placeholder="Stage Description"
                                  style={{ width: '300px' }}
                                />
                              ) : (
                                <span className="text-muted">{stage.main_item?.detail || 'No description'}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          {editStageIndex === stageIdx ? (
                            <>
                              <button 
                                className="btn btn-success btn-sm me-2" 
                                onClick={() => {
                                  const updated = [...currentData.stages];
                                  updated[stageIdx] = {...newStage};
                                  handleInputChange('stages', updated);
                                  setEditStageIndex(null);
                                  setNewStage({ stage_number: 0, main_item: { title: '', detail: '' }, items: [] });
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                              >
                                ✓
                              </button>
                              <button 
                                className="btn btn-secondary btn-sm me-2" 
                                onClick={() => {
                                  setEditStageIndex(null);
                                  setNewStage({ stage_number: 0, main_item: { title: '', detail: '' }, items: [] });
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                className="btn btn-outline-info btn-sm me-2" 
                                onClick={() => {
                                  const newExpanded = new Set(expandedStages);
                                  if (newExpanded.has(stageIdx)) {
                                    newExpanded.delete(stageIdx);
                                  } else {
                                    newExpanded.add(stageIdx);
                                  }
                                  setExpandedStages(newExpanded);
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                title={expandedStages.has(stageIdx) ? "Collapse Items" : "Expand Items"}
                              >
                                {expandedStages.has(stageIdx) ? "▼" : "▶"}
                              </button>
                              <button 
                                className="btn btn-outline-primary btn-sm me-2" 
                                onClick={() => {
                                  setEditStageIndex(stageIdx);
                                  setNewStage(stage);
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                title="Edit Stage"
                              >
                                ✏️
                              </button>
                              <button 
                                className="btn btn-outline-danger btn-sm" 
                                onClick={() => {
                                  const updated = [...currentData.stages];
                                  updated.splice(stageIdx, 1);
                                  handleInputChange('stages', updated);
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                title="Delete Stage"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Stage Items Section */}
                      {expandedStages.has(stageIdx) && (
                        <div className="stage-items-section mt-3 p-3 bg-light rounded">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Stage Items ({stage.items?.length || 0})</h6>
                            <Button 
                              icon="pi pi-plus" 
                              className="p-button-sm p-button-outlined" 
                              onClick={() => {
                                setEditStageItemIndex(null);
                                setNewStageItem({ title: '', detail: '' });
                              }}
                              label="Add Item"
                            />
                          </div>
                          
                          {/* Existing Items */}
                          {stage.items && stage.items.length > 0 && (
                            <div className="stage-items-list mb-3">
                              {stage.items.map((item: StageItem, itemIdx: number) => (
                                <div key={itemIdx} className="stage-item-detail mb-2 p-2 border rounded bg-white">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center flex-grow-1">
                                      <span className="drag-handle me-2" title="Drag to reorder">≡</span>
                                      <div className="flex-grow-1">
                                        {editStageItemIndex?.stage === stageIdx && editStageItemIndex?.item === itemIdx ? (
                                          <div className="row g-2">
                                            <div className="col-md-6">
                                              <InputText 
                                                value={newStageItem.title} 
                                                onChange={e => setNewStageItem(i => ({...i, title: e.target.value}))} 
                                                placeholder="Item Title" 
                                                className="w-100"
                                              />
                                            </div>
                                            <div className="col-md-6">
                                              <InputText 
                                                value={newStageItem.detail} 
                                                onChange={e => setNewStageItem(i => ({...i, detail: e.target.value}))} 
                                                placeholder="Item Detail" 
                                                className="w-100"
                                              />
                                            </div>
                                          </div>
                                        ) : (
                                          <div>
                                            <strong>{item.title}</strong>
                                            <div className="text-muted small">{item.detail}</div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      {editStageItemIndex?.stage === stageIdx && editStageItemIndex?.item === itemIdx ? (
                                        <>
                                          <button 
                                            className="btn btn-success btn-sm me-2" 
                                            onClick={() => {
                                              const updated = [...currentData.stages];
                                              updated[stageIdx].items[itemIdx] = {...newStageItem};
                                              handleInputChange('stages', updated);
                                              setEditStageItemIndex(null);
                                              setNewStageItem({ title: '', detail: '' });
                                            }}
                                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                          >
                                            ✓
                                          </button>
                                          <button 
                                            className="btn btn-secondary btn-sm me-2" 
                                            onClick={() => {
                                              setEditStageItemIndex(null);
                                              setNewStageItem({ title: '', detail: '' });
                                            }}
                                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                          >
                                            ✕
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button 
                                            className="btn btn-outline-primary btn-sm me-2" 
                                            onClick={() => {
                                              setEditStageItemIndex({stage: stageIdx, item: itemIdx});
                                              setNewStageItem(item);
                                            }}
                                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                            title="Edit Item"
                                          >
                                            ✏️
                                          </button>
                                          <button 
                                            className="btn btn-outline-danger btn-sm" 
                                            onClick={() => {
                                              const updated = [...currentData.stages];
                                              updated[stageIdx].items.splice(itemIdx, 1);
                                              handleInputChange('stages', updated);
                                            }}
                                            style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                            title="Delete Item"
                                          >
                                            🗑️
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Add/Edit Stage Item Form */}
                          {editStageItemIndex === null && (
                            <div className="card p-3">
                              <h6 className="mb-3">Add New Stage Item</h6>
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <label className="form-label">Item Title</label>
                                  <InputText 
                                    value={newStageItem.title} 
                                    onChange={e => setNewStageItem(i => ({...i, title: e.target.value}))} 
                                    placeholder="Enter item title" 
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-md-6">
                                  <label className="form-label">Item Detail</label>
                                  <InputText 
                                    value={newStageItem.detail} 
                                    onChange={e => setNewStageItem(i => ({...i, detail: e.target.value}))} 
                                    placeholder="Enter item detail" 
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-12">
                                  <Button 
                                    icon="pi pi-plus" 
                                    className="p-button-primary" 
                                    onClick={() => {
                                      if (newStageItem.title && newStageItem.detail) {
                                        const updated = [...currentData.stages];
                                        if (!updated[stageIdx].items) {
                                          updated[stageIdx].items = [];
                                        }
                                        updated[stageIdx].items.push({...newStageItem});
                                        handleInputChange('stages', updated);
                                        setNewStageItem({ title: '', detail: '' });
                                      }
                                    }}
                                    label="Add Item"
                                    disabled={!newStageItem.title || !newStageItem.detail}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Stage Form */}
              {editStageIndex === null && (
                <div className="card p-3">
                  <h6 className="mb-3">Add New Stage</h6>
                  <div className="row g-3">
                    <div className="col-md-2">
                      <label className="form-label">Stage #</label>
                      <InputNumber 
                        value={newStage.stage_number} 
                        onValueChange={e => setNewStage(s => ({...s, stage_number: e.value || 0}))} 
                        placeholder="Stage #" 
                        min={1} 
                        className="w-100"
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">Stage Title</label>
                      <InputText 
                        value={newStage.main_item?.title || ''} 
                        onChange={e => setNewStage(s => ({...s, main_item: { title: e.target.value, detail: s.main_item?.detail || '' }}))} 
                        placeholder="Enter stage title" 
                        className="w-100"
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">Stage Description</label>
                      <InputText 
                        value={newStage.main_item?.detail || ''} 
                        onChange={e => setNewStage(s => ({...s, main_item: { title: s.main_item?.title || '', detail: e.target.value }}))} 
                        placeholder="Enter stage description" 
                        className="w-100"
                      />
                    </div>
                    <div className="col-12">
                      <Button 
                        icon="pi pi-plus" 
                        className="p-button-primary" 
                        onClick={() => {
                          if (newStage.stage_number && newStage.main_item?.title) {
                            const updated = [...(currentData.stages || [])];
                            updated.push({...newStage, items: []});
                            handleInputChange('stages', updated);
                            setNewStage({ stage_number: (updated.length + 1), main_item: { title: '', detail: '' }, items: [] });
                          }
                        }}
                        label="Add Stage"
                        disabled={!newStage.stage_number || !newStage.main_item?.title}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Deliverables Management */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Deliverables</h5>
                <Button 
                  icon="pi pi-plus" 
                  className="p-button-sm p-button-outlined" 
                  onClick={() => {
                    setEditDeliverableIndex(null);
                    setNewDeliverable({ service: '', led_by: '', handoff: HandoffType.FULL_HANDOFF });
                  }}
                  label="Add Deliverable"
                />
              </div>
              
              {currentData.deliverables && currentData.deliverables.length > 0 && (
                <div className="deliverables-list mb-3">
                  {currentData.deliverables.map((d: Deliverable, idx: number) => (
                    <div key={idx} className="deliverable-item mb-2 p-3 border rounded">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center flex-grow-1">
                          <span className="drag-handle me-3" title="Drag to reorder">≡</span>
                          <div className="flex-grow-1">
                            {editDeliverableIndex === idx ? (
                              <div className="row g-2">
                                <div className="col-md-4">
                                  <InputText 
                                    value={newDeliverable.service} 
                                    onChange={e => setNewDeliverable(d => ({...d, service: e.target.value}))} 
                                    placeholder="Service" 
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-md-4">
                                  <Dropdown 
                                    value={newDeliverable.led_by} 
                                    options={partnerOptions} 
                                    onChange={e => setNewDeliverable(d => ({...d, led_by: e.value}))} 
                                    placeholder="Led By" 
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-md-4">
                                  <Dropdown 
                                    value={newDeliverable.handoff} 
                                    options={handoffOptions} 
                                    onChange={e => setNewDeliverable(d => ({...d, handoff: e.value}))} 
                                    placeholder="Handoff Type" 
                                    className="w-100"
                                  />
                                </div>
                              </div>
                            ) : (
                              <div>
                                <strong>{d.service}</strong> - {partnerOptions.find(p => p.value === d.led_by)?.label || d.led_by} ({d.handoff})
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          {editDeliverableIndex === idx ? (
                            <>
                              <button 
                                className="btn btn-success btn-sm me-2" 
                                onClick={() => {
                                  const updated = [...currentData.deliverables];
                                  updated[idx] = {...newDeliverable};
                                  handleInputChange('deliverables', updated);
                                  setEditDeliverableIndex(null);
                                  setNewDeliverable({ service: '', led_by: '', handoff: HandoffType.FULL_HANDOFF });
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                              >
                                ✓
                              </button>
                              <button 
                                className="btn btn-secondary btn-sm me-2" 
                                onClick={() => {
                                  setEditDeliverableIndex(null);
                                  setNewDeliverable({ service: '', led_by: '', handoff: HandoffType.FULL_HANDOFF });
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                className="btn btn-outline-primary btn-sm me-2" 
                                onClick={() => {
                                  setEditDeliverableIndex(idx);
                                  setNewDeliverable(d);
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                title="Edit Deliverable"
                              >
                                ✏️
                              </button>
                              <button 
                                className="btn btn-outline-danger btn-sm" 
                                onClick={() => {
                                  const updated = [...currentData.deliverables];
                                  updated.splice(idx, 1);
                                  handleInputChange('deliverables', updated);
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                title="Delete Deliverable"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Deliverable Form */}
              {editDeliverableIndex === null && (
                <div className="card p-3">
                  <h6 className="mb-3">Add New Deliverable</h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Service</label>                   <InputText 
                        value={newDeliverable.service} 
                        onChange={e => setNewDeliverable(d => ({...d, service: e.target.value}))} 
                        placeholder="Enter service name" 
                        className="w-100"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Led By</label>
                      <Dropdown 
                        value={newDeliverable.led_by} 
                        options={partnerOptions} 
                        onChange={e => setNewDeliverable(d => ({...d, led_by: e.value}))} 
                        placeholder="Select partner"                   className="w-100"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Handoff Type</label>
                      <Dropdown 
                        value={newDeliverable.handoff} 
                        options={handoffOptions} 
                        onChange={e => setNewDeliverable(d => ({...d, handoff: e.value}))} 
                        placeholder="Select handoff type" 
                        className="w-100"
                      />
                    </div>
                    <div className="col-12">
                      <Button 
                        icon="pi pi-plus" 
                        className="p-button-primary" 
                        onClick={() => {
                          if (newDeliverable.service && newDeliverable.led_by) {
                            const updated = [...(currentData.deliverables || [])];
                            updated.push({...newDeliverable});
                            handleInputChange('deliverables', updated);
                            setNewDeliverable({ service: '', led_by: '', handoff: HandoffType.FULL_HANDOFF });
                          }
                        }}
                        label="Add Deliverable"                   disabled={!newDeliverable.service || !newDeliverable.led_by}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      case 5:
        return (
          <div className="form-step active">
            <h4>Cost Breakdown</h4>
            
            {/* Cost Tables Management */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Cost Tables</h5>
                <Button 
                  icon="pi pi-plus" 
                  className="p-button-sm p-button-outlined" 
                  onClick={() => {
                    setEditTableIndex(null);
                    setNewTable({ service: '', monthly_cost: 0, monthly_cost_currency: CurrencyType.USD, months: 0, total_cost: 0, total_cost_currency: CurrencyType.USD });
                  }}
                  label="Add Cost Table"
                />
              </div>
              
              {currentData.tables && currentData.tables.length > 0 && (
                <div className="tables-list mb-3">
                  {currentData.tables.map((t: Table, idx: number) => (
                    <div key={idx} className="table-item mb-2 p-3 border rounded">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center flex-grow-1">
                          <span className="drag-handle me-3" title="Drag to reorder">≡</span>
                          <div className="flex-grow-1">
                            {editTableIndex === idx ? (
                              <div className="row g-2">
                                <div className="col-md-3">
                                  <InputText 
                                    value={newTable.service} 
                                    onChange={e => setNewTable(tbl => ({...tbl, service: e.target.value}))} 
                                    placeholder="Service" 
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-md-2">
                                  <InputNumber 
                                    value={newTable.monthly_cost} 
                                    onValueChange={e => setNewTable(tbl => ({
                                      ...tbl, 
                                      monthly_cost: e.value || 0, 
                                      total_cost: (e.value || 0) * (newTable.months || 0)
                                    }))} 
                                    placeholder="Monthly Cost" 
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-md-2">
                                  <Dropdown 
                                    value={newTable.monthly_cost_currency} 
                                    options={currencyOptions} 
                                    onChange={e => setNewTable(tbl => ({
                                      ...tbl, 
                                      monthly_cost_currency: e.value, 
                                      total_cost_currency: e.value
                                    }))} 
                                    placeholder="Currency" 
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-md-2">
                                  <InputNumber 
                                    value={newTable.months} 
                                    onValueChange={e => setNewTable(tbl => ({
                                      ...tbl, 
                                      months: e.value || 0, 
                                      total_cost: (newTable.monthly_cost || 0) * (e.value || 0)
                                    }))} 
                                    placeholder="Months" 
                                    className="w-100"
                                  />
                                </div>
                                <div className="col-md-2">
                                  <InputNumber 
                                    value={newTable.total_cost} 
                                    disabled 
                                    placeholder="Total Cost" 
                                    className="w-100"
                                  />
                                </div>
                              </div>
                            ) : (
                              <div>
                                <strong>{t.service}</strong> - {t.monthly_cost} {t.monthly_cost_currency} x {t.months} months = {t.total_cost} {t.total_cost_currency}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          {editTableIndex === idx ? (
                            <>
                              <button 
                                className="btn btn-success btn-sm me-2" 
                                onClick={() => {
                                  const updated = [...currentData.tables];
                                  updated[idx] = {...newTable};
                                  handleInputChange('tables', updated);
                                  setEditTableIndex(null);
                                  setNewTable({ service: '', monthly_cost: 0, monthly_cost_currency: CurrencyType.USD, months: 0, total_cost: 0, total_cost_currency: CurrencyType.USD });
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                              >
                                ✓
                              </button>
                              <button 
                                className="btn btn-secondary btn-sm me-2" 
                                onClick={() => {
                                  setEditTableIndex(null);
                                  setNewTable({ service: '', monthly_cost: 0, monthly_cost_currency: CurrencyType.USD, months: 0, total_cost: 0, total_cost_currency: CurrencyType.USD });
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                className="btn btn-outline-primary btn-sm me-2" 
                                onClick={() => {
                                  setEditTableIndex(idx);
                                  setNewTable(t);
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                title="Edit Cost Table"
                              >
                                ✏️
                              </button>
                              <button 
                                className="btn btn-outline-danger btn-sm" 
                                onClick={() => {
                                  const updated = [...currentData.tables];
                                  updated.splice(idx, 1);
                                  handleInputChange('tables', updated);
                                }}
                                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '6px' }}
                                title="Delete Cost Table"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Cost Table Form */}
              {editTableIndex === null && (
                <div className="card p-3">
                  <h6 className="mb-3">Add New Cost Table</h6>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Service</label>
                      <InputText 
                        value={newTable.service} 
                        onChange={e => setNewTable(t => ({...t, service: e.target.value}))} 
                        placeholder="Enter service name" 
                        className="w-100"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Monthly Cost</label>
                      <InputNumber 
                        value={newTable.monthly_cost} 
                        onValueChange={e => setNewTable(t => ({
                          ...t, 
                          monthly_cost: e.value || 0, 
                          total_cost: (e.value || 0) * (newTable.months || 0)
                        }))} 
                        placeholder="Monthly Cost" 
                        className="w-100"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Currency</label>
                      <Dropdown 
                        value={newTable.monthly_cost_currency} 
                        options={currencyOptions} 
                        onChange={e => setNewTable(t => ({
                          ...t, 
                          monthly_cost_currency: e.value, 
                          total_cost_currency: e.value
                        }))} 
                        placeholder="Select currency" 
                        className="w-100"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Months</label>
                      <InputNumber 
                        value={newTable.months} 
                        onValueChange={e => setNewTable(t => ({
                          ...t, 
                          months: e.value || 0, 
                          total_cost: (newTable.monthly_cost || 0) * (e.value || 0)
                        }))} 
                        placeholder="Months" 
                        min={1}
                        className="w-100"
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Total Cost</label>
                      <InputNumber 
                        value={newTable.total_cost} 
                        disabled 
                        placeholder="Auto-calculated" 
                        className="w-100"
                      />
                    </div>
                    <div className="col-12">
                      <Button 
                        icon="pi pi-plus" 
                        className="p-button-primary" 
                        onClick={() => {
                          if (newTable.service && newTable.monthly_cost && newTable.months) {
                            const updated = [...(currentData.tables || [])];
                            updated.push({...newTable});
                            handleInputChange('tables', updated);
                            setNewTable({ service: '', monthly_cost: 0, monthly_cost_currency: CurrencyType.USD, months: 0, total_cost: 0, total_cost_currency: CurrencyType.USD });
                          }
                        }}
                        label="Add Cost Table"
                        disabled={!newTable.service || !newTable.monthly_cost || !newTable.months}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="form-step active">
            <h4>Review & Submit</h4>
            <Card>
              <div className="review-section">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Title level={5}>Basic Information</Title>
                  <Button 
                    icon="pi pi-pencil" 
                    className="p-button-text p-button-sm" 
                    onClick={() => setCurrentStep(1)}
                    label="Edit"
                  />
                </div>
                <Row gutter={[16, 8]}>
                  <Col span={8}>
                    <Text strong>Project:</Text> {getProjectName(currentData.project_id)}
                  </Col>
                  <Col span={8}>
                    <Text strong>Status:</Text> <Tag color={currentData.status === 'PENDING' ? 'orange' : currentData.status === 'ACCEPTED' ? 'green' : 'red'}>{currentData.status}</Tag>
                  </Col>
                  <Col span={8}>
                    <Text strong>Project Lead:</Text> {getLeadName(currentData.project_lead_id)}
                  </Col>
                </Row>
              </div>

              <Divider />

              <div className="review-section">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Title level={5}>Financial Information</Title>
                  <Button 
                    icon="pi pi-pencil" 
                    className="p-button-text p-button-sm" 
                    onClick={() => setCurrentStep(2)}
                    label="Edit"
                  />
                </div>
                <Row gutter={[16, 8]}>
                  <Col span={8}>
                    <Text strong>Price:</Text> {currentData.currency_type} {currentData.price?.toLocaleString()}
                  </Col>
                  <Col span={8}>
                    <Text strong>Timeline:</Text> {currentData.expected_project_time} weeks
                  </Col>
                  <Col span={8}>
                    <Text strong>Currency:</Text> {currentData.currency_type}
                  </Col>
                </Row>
              </div>

              <Divider />

              <div className="review-section">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Title level={5}>Content</Title>
                  <Button 
                    icon="pi pi-pencil" 
                    className="p-button-text p-button-sm" 
                    onClick={() => setCurrentStep(3)}
                    label="Edit"
                  />
                </div>
                <Row gutter={[16, 8]}>
                  <Col span={24}>
                    <Text strong>Overview:</Text>
                    <div className="mt-1">{currentData.overview}</div>
                  </Col>
                  <Col span={24}>
                    <Text strong>Scope of Work:</Text>
                    <div className="mt-1">{currentData.scope_of_work}</div>
                  </Col>
                  <Col span={24}>
                    <Text strong>Quotation Details:</Text>
                    <div className="mt-1">{currentData.quotation_details}</div>
                  </Col>
                  {currentData.not_included_in_price && (
                    <Col span={24}>
                      <Text strong>Not Included in Price:</Text>
                      <div className="mt-1">{currentData.not_included_in_price}</div>
                    </Col>
                  )}
                </Row>
              </div>

              <Divider />

              <div className="review-section">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Title level={5}>Planning</Title>
                  <Button 
                    icon="pi pi-pencil" 
                    className="p-button-text p-button-sm" 
                    onClick={() => setCurrentStep(4)}
                    label="Edit"
                  />
                </div>
                
                <div className="mb-3">
                  <Text strong>Stages ({currentData.stages?.length || 0}):</Text>
                  {currentData.stages && currentData.stages.length > 0 && (
                    <div className="mt-2">
                      {currentData.stages.map((stage: Stage, idx: number) => (
                        <div key={idx} className="mb-3 p-3 border rounded bg-light">
                          <div className="mb-2">
                            <Text strong>Stage {stage.stage_number}: {stage.main_item?.title}</Text>
                            <div className="text-muted small">{stage.main_item?.detail}</div>
                          </div>
                          
                          {stage.items && stage.items.length > 0 && (
                            <div className="mt-2">
                              <Text strong>Items ({stage.items.length}):</Text>
                              <ul className="mt-1">
                                {stage.items.map((item: StageItem, itemIdx: number) => (
                                  <li key={itemIdx} className="mb-1">
                                    <Text strong>{item.title}</Text>
                                    <div className="text-muted small">{item.detail}</div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <Text strong>Deliverables ({currentData.deliverables?.length || 0}):</Text>
                  {currentData.deliverables && currentData.deliverables.length > 0 && (
                    <ul className="mt-1">
                      {currentData.deliverables.map((d: Deliverable, idx: number) => (
                        <li key={idx}>
                          <Text>{d.service} - {partnerOptions.find(p => p.value === d.led_by)?.label || d.led_by} ({d.handoff})</Text>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <Divider />

              <div className="review-section">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Title level={5}>Cost Breakdown</Title>
                  <Button 
                    icon="pi pi-pencil" 
                    className="p-button-text p-button-sm" 
                    onClick={() => setCurrentStep(5)}
                    label="Edit"
                  />
                </div>
                
                <Text strong>Tables ({currentData.tables?.length || 0}):</Text>
                {currentData.tables && currentData.tables.length > 0 && (
                  <ul className="mt-1">
                    {currentData.tables.map((t: Table, idx: number) => (
                      <li key={idx}>
                        <Text>{t.service} - {t.monthly_cost} {t.monthly_cost_currency} x {t.months} months = {t.total_cost} {t.total_cost_currency}</Text>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  const getProjectName = (projectId: string) => {
    const project = projects?.find((p: any) => p.id === projectId);
    return project?.name || projectId;
  };

  const getLeadName = (leadId: string) => {
    const lead = leads?.find((l: any) => l.id === leadId);
    return lead ? `${lead.first_name} ${lead.last_name}` : leadId;
  };

  const progressValue = (completedSteps.size / STEPS.length) * 100;
  return (
    <>
      <style>{style}</style>
      <Toast ref={toast} />

      <div className={`modal fade ${isAddModalOpen || isEditModalOpen ? 'show' : ''}`}
           style={{ display: isAddModalOpen || isEditModalOpen ? 'block' : 'none', zIndex: 1050 }}>
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {isEditModalOpen ? 'Edit Proposal' : 'Create New Proposal'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsEditModalOpen(false);
                }}
              ></button>
            </div>

            <div className="modal-body">
              {renderStepIndicator()}
              
              {/* --- 3. Progress bar: ensure no text is shown --- */}
              <ProgressBar value={progressValue} style={{ height: '6px', marginBottom: '2rem' }} showValue={false} />
              
              {renderStepContent()}

              <div className="form-actions">
                <Button
                  label="Previous"
                  icon="pi pi-chevron-left"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="p-button-secondary"
                />

                <div>
                  <Button
                    label="Save Draft"
                    icon="pi pi-save"
                    onClick={() => {
                      const draftData = isEditModalOpen ? updatedProposal : proposal;
                      localStorage.setItem('proposal_draft', JSON.stringify(draftData));
                      setShowDraftSaved(true);
                      setTimeout(() => setShowDraftSaved(false), 2000);
                    }}
                    className="p-button-outlined p-button-secondary me-2"
                  />

                  {currentStep === STEPS.length ? (
                    <Button
                      label={isEditModalOpen ? 'Update Proposal' : 'Create Proposal'}
                      icon="pi pi-check"
                      onClick={handleSubmit}
                      loading={isLoading}
                      className="p-button-success"
                    />
                  ) : (
                    <Button
                      label="Next"
                      icon="pi pi-chevron-right"
                      onClick={nextStep}
                      className="p-button-primary"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDraftSaved && (
        <div className="draft-save-indicator show">
          <Icon.Check size={16} className="me-2" />
          Draft saved successfully
        </div>
      )}
    </>
  );
};

export default ProposalsModal;
